/* import __COLOCATED_TEMPLATE__ from './main-application-loading-screen.hbs'; */
/* RESPONSIBLE TEAM: team-frontend-tech */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { runTask } from 'ember-lifeline';
import ENV from 'embercom/config/environment';
import EmbercomBootDataModule from 'embercom/lib/embercom-boot-data';
import { isStandaloneRoute } from 'embercom/helpers/standalone/standalone-routes';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import type SuiteMigrationService from 'embercom/services/suite-migration-service';
import type ApplicationInstance from '@ember/application/instance';

interface Signature {
  Args: {
    hideNavbarSkeleton?: boolean;
  };
}

export default class MainApplicationLoadingScreen extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;
  @tracked showFollowUp = false;

  @service declare suiteMigrationService: SuiteMigrationService;

  @tracked isLoadingStandaloneCopilotRoute = false;
  @tracked isLoadingStandaloneApp = false;

  constructor(owner: ApplicationInstance, args: Signature['Args']) {
    super(owner, args);

    /* eslint-disable ember/no-private-routing-service */
    let router = owner.lookup('router:main');
    let loadingTarget: string | undefined = router?._routerMicrolib?.activeTransition?.targetName;

    if (loadingTarget === 'apps.app.copilot-frame' || loadingTarget === 'frame-login') {
      this.isLoadingStandaloneCopilotRoute = true;
    }

    if (loadingTarget && isStandaloneRoute(loadingTarget)) {
      this.isLoadingStandaloneApp = true;
    }

    runTask(this, () => (this.showFollowUp = true), ENV.APP._5000MS);
  }

  get shouldShowStandaloneNavbar() {
    return (
      this.isFinStandaloneApp &&
      this.isLoadingStandaloneApp &&
      !this.suiteMigrationService.forceSuiteVersion
    );
  }

  private get isFinStandaloneApp() {
    // @ts-expect-error
    return window?.__embercom_boot_data?.is_fin_standalone_app ?? false;
  }

  get initialText() {
    if (!this.intl.hasLoadedCurrentLocale) {
      return;
    }

    return `${this.chosenText.initial}…`;
  }

  get followUp() {
    if (!this.intl.hasLoadedCurrentLocale) {
      return;
    }

    return this.chosenText.followUp;
  }

  get shouldShowFollowUp() {
    return this.showFollowUp;
  }

  private get texts() {
    let baseTexts = [
      {
        initial: this.intl.t('components.main-application-loading-screen.getting-everything-ready'),
        followUp: this.intl.t('components.main-application-loading-screen.just-a-few-more-seconds'),
      },
      {
        initial: this.intl.t(
          'components.main-application-loading-screen.putting-everything-together',
        ),
        followUp: this.intl.t('components.main-application-loading-screen.ready-in-a-second'),
      },
      {
        initial: this.intl.t('components.main-application-loading-screen.right-with-you'),
        followUp: this.intl.t(
          'components.main-application-loading-screen.thanks-for-your-patience',
        ),
      },
    ];

    let embercomTexts = [
      {
        initial: this.intl.t('components.main-application-loading-screen.loading-your-info'),
        followUp: this.intl.t(
          'components.main-application-loading-screen.just-a-second-or-two-more',
        ),
      },
      {
        initial: this.intl.t('components.main-application-loading-screen.loading-your-data'),
        followUp: this.intl.t(
          'components.main-application-loading-screen.this-might-take-a-second',
        ),
      },
    ];
    let isEmbercom = !EmbercomBootDataModule.isBootedInPublicPage();
    return isEmbercom ? baseTexts.concat(embercomTexts) : baseTexts;
  }

  private get chosenText() {
    let index = Date.now() % this.texts.length;
    return this.texts[index];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    MainApplicationLoadingScreen: typeof MainApplicationLoadingScreen;
    'main-application-loading-screen': typeof MainApplicationLoadingScreen;
  }
}
